/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/recomputeOnEdit.js
 *
 * G-B-FIX: scoped SUV recompute — only the edited (ROI, timepoint) pairs are
 * re-sampled. Full PET voxel walk for all N×T pairs is no longer triggered on
 * every polygon edit.
 *
 * Two call shapes:
 *
 *   // Body Part / Tracking Type change — no SUV recompute, just re-query thresholds
 *   scheduleRecompute({ affectedPairs: null, imageSets, criteria, structureSetUID, setROIContours });
 *
 *   // Contour edit — recompute voxels for specific pairs, then PSMA+ vol
 *   scheduleRecompute({ affectedPairs: [{ROINumber, ctSeriesUID}, ...], ... });
 *
 * In-flight / trailing-edge queue:
 *   If a second call arrives while computing, it is stored in `pending`.
 *   When affectedPairs are present in both the running call and the incoming
 *   call, they are merged (dedup by ROINumber@@ctSeriesUID) so no edit is lost.
 *   After the running compute finishes, `pending` fires immediately.
 *
 * PSMA+ vol step is always run (full pass, cheap) — guarantees consistency for
 * liver-reference cascade: liver histogram updated → threshold recomputed →
 * every soft-tissue lesion's existing histogram re-queried against new threshold.
 *
 * Param rename: SeriesInstanceUID → structureSetUID (the ID used is the
 * RTSTRUCT series UID, not the CT series UID; old name was misleading).
 */

import cornerstoneTools from 'cornerstone-tools';
import computePsmaPosVolumesFromBridge from './computePsmaPosVolumesFromBridge';
import computeRoiSuvForPairs from '../suv/computeRoiSuvForPairs';

let inflight = false;
let pending = null;

/**
 * Merge two affectedPairs arrays, deduplicating by ROINumber + ctSeriesUID.
 */
function mergePairs(a, b) {
  const seen = new Set();
  const out = [];
  for (const p of [...a, ...b]) {
    const k = `${p.ROINumber}@@${p.ctSeriesUID}`;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(p);
    }
  }
  return out;
}

/**
 * Schedule a recompute, coalescing rapid calls.
 * Latest non-null affectedPairs wins; pairs from multiple pending calls
 * are merged so no edit is silently dropped.
 */
export async function scheduleRecompute(args) {
  if (inflight) {
    if (!pending) {
      pending = args;
    } else {
      // Merge affectedPairs if both have them; otherwise keep whichever is non-null
      const mergedPairs =
        pending.affectedPairs && args.affectedPairs
          ? mergePairs(pending.affectedPairs, args.affectedPairs)
          : pending.affectedPairs || args.affectedPairs;
      pending = { ...args, affectedPairs: mergedPairs };
    }
    return;
  }
  await _run(args);
}

async function _run(args) {
  inflight = true;
  try {
    await _execute(args);
    if (pending) {
      const next = pending;
      pending = null;
      await _execute(next);
    }
  } finally {
    inflight = false;
  }
}

async function _execute({
  affectedPairs,
  imageSets,
  criteria,
  structureSetUID,
  setROIContours,
}) {
  // eslint-disable-next-line no-console
  console.log('[RECIP-RECOMPUTE]', {
    suvPairs: affectedPairs ? affectedPairs.length : 0,
  });

  const editModule = cornerstoneTools.getModule('rtstruct-edit');

  // SUV step — only when specific pairs are provided (contour edit path)
  if (affectedPairs && affectedPairs.length > 0) {
    await computeRoiSuvForPairs({
      affectedPairs,
      imageSets,
      getStructureSet: uid => editModule.getters.structureSet(uid),
    });
  }

  // PSMA+ vol step — always run full pass (cheap; guarantees liver cascade)
  const latestROIs =
    editModule.getters.structureSet(structureSetUID)?.ROIContours || [];
  const { ROIContours: updated } = computePsmaPosVolumesFromBridge({
    ROIContours: latestROIs,
    imageSets,
    criteria,
  });

  // React state update — single call, no per-ROI re-render
  setROIContours(updated);
  // Push to editModule store (not React state — loop is fine)
  updated.forEach(roi => editModule.setters.updateROI(structureSetUID, roi));
}
